import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TerminalQuestLeaderboard({ questId, questTitle, onClose }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [questId]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/leaderboard/terminal-quest/${questId}`
      );
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching terminal quest leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}º`;
  };

  const getCompletionColor = (percentage) => {
    if (percentage === 100) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <span>💻</span>
                Clasificación Terminal Quest
              </h2>
              <p className="text-green-100 mt-1">{questTitle}</p>
              <p className="text-xs text-green-100 mt-1">
                Ranking basado en niveles completados y velocidad de finalización
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              <p className="mt-4 text-gray-600">Cargando clasificación...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💻</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay progreso aún
              </h3>
              <p className="text-gray-500">
                Ningún estudiante ha comenzado este Terminal Quest todavía
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Pos.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Estudiante
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Nivel Actual
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Progreso
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Completado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leaderboard.map((entry) => (
                    <tr
                      key={entry.student_id}
                      className={`
                        hover:bg-gray-50 transition
                        ${entry.rank === 1 ? 'bg-yellow-50' : ''}
                        ${entry.rank === 2 ? 'bg-gray-50' : ''}
                        ${entry.rank === 3 ? 'bg-orange-50' : ''}
                      `}
                    >
                      {/* Rank */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-2xl font-bold">
                          {getMedalEmoji(entry.rank)}
                        </div>
                      </td>

                      {/* Student */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {entry.student_picture && (
                            <img
                              src={entry.student_picture}
                              alt={entry.student_name}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                          )}
                          <div>
                            <div className="text-sm font-bold text-gray-900">
                              {entry.student_name}
                            </div>
                            {entry.is_completed && (
                              <div className="text-xs text-green-600 font-semibold">
                                ✓ Completado
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Current Level */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-bold text-gray-900">
                          Nivel {entry.current_level}
                        </div>
                      </td>

                      {/* Progress */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-col items-center">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {entry.completed_levels} / {entry.total_levels}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                entry.completion_percentage === 100
                                  ? 'bg-green-600'
                                  : 'bg-blue-600'
                              }`}
                              style={{ width: `${entry.completion_percentage}%` }}
                            ></div>
                          </div>
                          <div className={`text-xs mt-1 font-semibold ${getCompletionColor(entry.completion_percentage)}`}>
                            {entry.completion_percentage.toFixed(0)}%
                          </div>
                        </div>
                      </td>

                      {/* Completed Status */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {entry.is_completed ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            ✓ Sí
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                            En progreso
                          </span>
                        )}
                      </td>

                      {/* Efficiency Score */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="text-lg font-bold text-green-600">
                          {entry.efficiency_score}
                        </div>
                        <div className="text-xs text-gray-500">puntos</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-bold">{leaderboard.filter(e => e.is_completed).length}</span> de{' '}
            <span className="font-bold">{leaderboard.length}</span> estudiantes completaron el quest
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default TerminalQuestLeaderboard;
